/*
 * svnty-fit-engine.js - the size finder's arithmetic.
 * window.SvntyFit = { estimateBody, recommend, fitOf, constants }.
 * Pure functions, no DOM. Units: cm and kg. Charts are measured flat (one side).
 */
(function () {
  'use strict';

  var constants = {
    // Body model, men: circumference = a + h * height + w * weight + age * AGE (cm).
    AGE: 30,
    BODY: {
      chest: { a: 98.19, h: -0.269, w: 0.602, age: 0.111 },
      waist: { a: 92.76, h: -0.396, w: 0.759, age: 0.198 },
      hip: { a: 83.17, h: -0.150, w: 0.544, age: -0.047 },
      thigh: { a: 63.78, h: -0.201, w: 0.429, age: -0.088 },
      crotch: { a: -20.64, h: 0.619, w: -0.038, age: 0 }
    },
    // Inseam target: crotch height minus this (cm).
    INSEAM_DROP: 3,
    // Calibration offsets added to the body model (cm).
    CAL: { chest: 0, waist: 1.5, hip: 0 },
    // Body shape answers: offsets in cm.
    SHAPE: {
      belly: { flat: { waist: -4.4, chest: -0.5 }, avg: { waist: 0, chest: 0 }, full: { waist: 4.4, chest: 0.3 } },
      shoulders: { narrow: { chest: -1.4, shoulder: -1.6 }, avg: { chest: 0, shoulder: 0 }, broad: { chest: 1.3, shoulder: 1.6 } }
    },
    // Spread of a size estimate per measure (cm), used in the size probability.
    SIGMA: { chest: 3.3, waist: 4.2, hip: 2.8 },
    // Ease by garment, full circumference minus body (cm): [closer, true, relaxed].
    EASE: {
      tee: { chest: [6, 10, 16] },
      polo: { chest: [6, 10, 16] },
      shirt: { chest: [10, 14, 20] },
      knit: { chest: [0, 4, 10] },
      sweat: { chest: [12, 18, 26] },
      hoodie: { chest: [12, 18, 26] },
      jacket: { chest: [14, 20, 26] },
      vest: { chest: [8, 12, 16] },
      chino: { waist: [1, 2, 4], hip: [6, 8, 12] },
      jeans: { waist: [0, 1, 3], hip: [2, 4, 6] },
      pleated: { hip: [6, 8, 12] },
      joggers: { hip: [6, 8, 12] }
    },
    // Cut of the garment: shift added to every ease column (cm).
    EASE_CLASS: { slim: -4, regular: 0, relaxed: 6, oversized: 10 },
    // High stretch fabric: ease removed (cm).
    STRETCH_HIGH: 2,
    // Effective ease never goes below this (cm).
    EASE_FLOOR: 0,
    // The garment's own cut: share of the gap between the ease at the anchor size and the table's true ease
    // that moves the ease columns, and the limits of that gap (cm).
    anchorPull: 0.6,
    PULL_CLAMP: [-8, 10],
    // Tops: chest to waist taper allowed before the waist decides (cm).
    TAPER: { shirt: 4 },
    // Top length target as a share of height.
    LENGTH_SHARE: { tee: 0.40, polo: 0.40, knit: 0.40, sweat: 0.40, hoodie: 0.40, vest: 0.40, shirt: 0.43, jacket: 0.42 },
    // Length off by more than this reads long or short (cm).
    LENGTH_TOL: 4,
    // Fit words from margin = garment - (body + true ease): below SNUG is snug, above ROOMY is roomy (cm).
    WORD: { snug: -2, roomy: 3 },
    // Runner-up probability that makes the answer "between two sizes".
    BETWEEN: 0.30,
    // Confidence bands on the best size probability.
    CONF: { high: 0.6, medium: 0.4 },
    // Accepted input range.
    RANGE: { h: [140, 215], w: [35, 200] },
    // Garment by product type (used when a chart does not name it); pants whose title says pleated are pleated.
    GARMENT_BY_TYPE: {
      't-shirts': 'tee', polos: 'polo', shirts: 'shirt', knitwear: 'knit', sweatshirts: 'sweat', hoodies: 'hoodie',
      jackets: 'jacket', vests: 'vest', pants: 'chino', jeans: 'jeans', joggers: 'joggers'
    },
    // Garment by title words, when neither the chart nor the type names it.
    GARMENT_BY_WORD: [
      ['pleated', 'pleated'], ['jogger', 'joggers'], ['jean', 'jeans'], ['chino', 'chino'], ['pants', 'chino'],
      ['trouser', 'chino'], ['hoodie', 'hoodie'], ['sweatshirt', 'sweat'], ['vest', 'vest'], ['jacket', 'jacket'],
      ['bomber', 'jacket'], ['polo', 'polo'], ['t-shirt', 'tee'], ['tee', 'tee'], ['knit', 'knit'], ['pullover', 'knit'],
      ['crewneck', 'sweat'], ['shirt', 'shirt']
    ],
    // Old chart column names to measure keys.
    COLUMN_KEYS: {
      width: 'chest', chest: 'chest', length: 'length', shoulder: 'shoulder', sleeve: 'sleeve',
      waist: 'waist', 'waist (relaxed)': 'waist', 'waist (stretched)': 'waist_max', hip: 'hip', thigh: 'thigh',
      'front rise': 'rise', rise: 'rise', inseam: 'inseam', 'leg opening': 'leg_opening'
    },
    // Grading rules for measures a chart does not carry (flat cm).
    GRADE: { waistMax: 1.12, thigh: 0.62, legOpening: 0.60 }
  };

  var PREF = { snug: 0, closer: 0, 'true': 1, loose: 2, relaxed: 2 };
  var STEPS = ['low', 'medium', 'high'];

  function r1(x) { return Math.round(x * 10) / 10; }
  function r3(x) { return Math.round(x * 1000) / 1000; }
  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (v == null) return null;
    var n = parseFloat(String(v).replace(',', '.'));
    return isFinite(n) ? n : null;
  }
  function isArr(a) { return Object.prototype.toString.call(a) === '[object Array]'; }

  // Normal CDF through the error function (rational approximation, error below 1.5e-7).
  function erf(x) {
    var s = x < 0 ? -1 : 1;
    x = Math.abs(x);
    var t = 1 / (1 + 0.3275911 * x);
    var y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
    return s * y;
  }
  function phi(z) { return 0.5 * (1 + erf(z / Math.SQRT2)); }

  function norm(input) {
    input = input || {};
    var fit = input.fit === 'snug' || input.fit === 'loose' ? input.fit : 'true';
    var belly = input.belly === 'flat' || input.belly === 'full' ? input.belly : 'avg';
    var sh = input.shoulders === 'narrow' || input.shoulders === 'broad' ? input.shoulders : 'avg';
    return { h: num(input.h), w: num(input.w), fit: fit, belly: belly, shoulders: sh, type: input.type || '', title: input.title || '' };
  }

  function lin(c, h, w) { return c.a + c.h * h + c.w * w + c.age * constants.AGE; }

  function estimateBody(input) {
    var i = norm(input);
    if (i.h == null || i.w == null) return null;
    var B = constants.BODY, C = constants.CAL;
    var be = constants.SHAPE.belly[i.belly], so = constants.SHAPE.shoulders[i.shoulders];
    return {
      chest: r1(lin(B.chest, i.h, i.w) + C.chest + be.chest + so.chest),
      waist: r1(lin(B.waist, i.h, i.w) + C.waist + be.waist),
      hip: r1(lin(B.hip, i.h, i.w) + C.hip),
      thigh: r1(lin(B.thigh, i.h, i.w)),
      inseam: r1(lin(B.crotch, i.h, i.w) - constants.INSEAM_DROP)
    };
  }

  // ---------- chart handling ----------

  // Garment class: the chart's own key, else the product type, else the title words, else the chart's family.
  function garmentFor(chart, type, title) {
    if (chart && chart.garment && constants.EASE[chart.garment]) return chart.garment;
    var t = String(type || '').toLowerCase().trim(), ti = String(title || '').toLowerCase(), k;
    var fam = chart ? chart.family : '';
    var tops = fam === 'tops';
    if (constants.GARMENT_BY_TYPE[t]) {
      var g = constants.GARMENT_BY_TYPE[t];
      if (g === 'chino' && (ti.indexOf('pleated') >= 0 || (chart && chart.stretch === 'elastic-waist'))) return 'pleated';
      return g;
    }
    for (k = 0; k < constants.GARMENT_BY_WORD.length; k++) {
      var gw = constants.GARMENT_BY_WORD[k][1], isTop = !!constants.EASE[gw].chest;
      if (ti.indexOf(constants.GARMENT_BY_WORD[k][0]) >= 0 && (!fam || isTop === tops)) return gw;
    }
    if (fam === 'jeans') return 'jeans';
    if (fam === 'bottoms-waist') return 'chino';
    if (fam === 'bottoms-letter') return chart.stretch === 'elastic-waist' ? 'pleated' : 'chino';
    return 'tee';
  }

  // Old chart {tables:[{columns, rows:[{size, values}]}]} to the current shape.
  function fromV1(v1, type, title) {
    var t = v1 && isArr(v1.tables) ? v1.tables[0] : null;
    if (!t || !isArr(t.columns) || !isArr(t.rows)) return null;
    var sizes = [], measures = {}, keys = [], relaxedWaist = false, derived = [];
    for (var c = 0; c < t.columns.length; c++) {
      var name = String(t.columns[c]).toLowerCase().replace(/\s*\(cm\)\s*/, '').trim();
      if (name === 'waist (relaxed)') relaxedWaist = true;
      keys.push(constants.COLUMN_KEYS[name] || null);
    }
    for (var r = 0; r < t.rows.length; r++) sizes.push(String(t.rows[r].size));
    for (var k = 0; k < keys.length; k++) {
      if (!keys[k]) continue;
      var arr = [];
      for (var j = 0; j < t.rows.length; j++) arr.push(num((t.rows[j].values || [])[k]));
      measures[keys[k]] = arr;
    }
    var tt = String(type || '').toLowerCase();
    var family, stretch = 'none';
    if (measures.chest) family = 'tops';
    else if (tt === 'jeans') family = 'jeans';
    else if (/^\d+$/.test(sizes[0] || '')) family = parseInt(sizes[0], 10) <= 34 ? 'jeans' : 'bottoms-waist';
    else family = 'bottoms-letter';
    if (relaxedWaist || tt === 'joggers') stretch = 'elastic-waist';
    else if (family === 'jeans') stretch = 'low';
    else if (tt === 'knitwear') stretch = 'high';
    var G = constants.GRADE;
    if (stretch === 'elastic-waist' && measures.waist && !measures.waist_max) {
      measures.waist_max = measures.waist.map(function (x) { return x == null ? null : Math.round(x * G.waistMax * 2) / 2; });
      derived.push('waist_max');
    }
    if (family !== 'tops' && measures.hip && !measures.thigh) {
      measures.thigh = measures.hip.map(function (x) { return x == null ? null : Math.round(x * G.thigh * 2) / 2; });
      derived.push('thigh');
    }
    var ch = { v: 2, family: family, unit: v1.unit || 'cm', flat: true, stretch: stretch, ease_class: 'regular',
      anchor: { size: v1.anchor_size || null }, sizes: sizes, measures: measures, derived: derived };
    ch.garment = garmentFor(ch, type, title);
    return ch;
  }

  function toV2(chart, type, title) {
    if (!chart) return null;
    if (typeof chart === 'string') { try { chart = JSON.parse(chart); } catch (e) { return null; } }
    if (chart.v === 2 && isArr(chart.sizes) && chart.measures) {
      var c = {};
      for (var k in chart) if (Object.prototype.hasOwnProperty.call(chart, k)) c[k] = chart[k];
      c.garment = garmentFor(chart, type, title);
      return c;
    }
    return fromV1(chart, type, title);
  }

  // The measure that decides the size: chest for tops, the hip under an elastic waist, else the waist.
  function decidingKey(chart) {
    if (chart.family === 'tops') return 'chest';
    return chart.stretch === 'elastic-waist' ? 'hip' : 'waist';
  }

  // Ease for the garment's cut at the chart's anchor: how much room the anchor size gives the anchor body.
  function anchorShift(chart, measure, trueEase) {
    var a = chart.anchor;
    if (!a || a.size == null || num(a.h) == null || num(a.w) == null || !chart.measures) return 0;
    var k = chart.sizes.indexOf(String(a.size)), G = full(chart.measures[measure]);
    if (k < 0 || !G || G.length !== chart.sizes.length) return 0;
    var body = estimateBody({ h: num(a.h), w: num(a.w) });
    if (!body || body[measure] == null) return 0;
    var gap = G[k] - body[measure] - trueEase, c = constants.PULL_CLAMP;
    return constants.anchorPull * Math.min(c[1], Math.max(c[0], gap));
  }

  function easeFor(chart, garment, measure) {
    var e = constants.EASE[garment] && constants.EASE[garment][measure];
    if (!e) return null;
    var shift = constants.EASE_CLASS[chart.ease_class] || 0;
    if (chart.stretch === 'high') shift -= constants.STRETCH_HIGH;
    if (measure === decidingKey(chart)) shift += anchorShift(chart, measure, e[1] + shift);
    var f = constants.EASE_FLOOR;
    return [Math.max(f, e[0] + shift), Math.max(f, e[1] + shift), Math.max(f, e[2] + shift)];
  }

  // Probability that each size is the right one for requirement R (full cm).
  function probs(G, R, sigma) {
    var n = G.length, p = [];
    for (var i = 0; i < n; i++) {
      var lo = i === 0 ? -Infinity : (G[i - 1] + G[i]) / 2;
      var hi = i === n - 1 ? Infinity : (G[i] + G[i + 1]) / 2;
      var a = lo === -Infinity ? 0 : phi((lo - R) / sigma);
      var b = hi === Infinity ? 1 : phi((hi - R) / sigma);
      p.push(Math.max(0, b - a));
    }
    return p;
  }
  function argmax(p) {
    var k = -1;
    for (var i = 0; i < p.length; i++) if (p[i] > 0 && (k < 0 || p[i] > p[k])) k = i;
    return k;
  }
  function full(arr) {
    if (!isArr(arr)) return null;
    var out = [];
    for (var i = 0; i < arr.length; i++) { var v = num(arr[i]); if (v == null) return null; out.push(v * 2); }
    return out;
  }
  function flatArr(arr) {
    if (!isArr(arr)) return null;
    var out = [];
    for (var i = 0; i < arr.length; i++) { var v = num(arr[i]); if (v == null) return null; out.push(v); }
    return out;
  }
  function word(m) { return m < constants.WORD.snug ? 'snug' : (m > constants.WORD.roomy ? 'roomy' : 'true'); }
  function lenWord(d) { return d > constants.LENGTH_TOL ? 'long' : (d < -constants.LENGTH_TOL ? 'short' : 'true'); }
  function down(conf) { var i = STEPS.indexOf(conf); return STEPS[Math.max(0, i - 1)]; }

  // Everything the decision needs, per measure.
  function setup(chart, input) {
    var i = norm(input);
    var ch = toV2(chart, i.type, i.title);
    if (!ch || !isArr(ch.sizes) || !ch.sizes.length) return { err: 'no-chart' };
    if (i.h == null || i.w == null || i.h < constants.RANGE.h[0] || i.h > constants.RANGE.h[1] ||
        i.w < constants.RANGE.w[0] || i.w > constants.RANGE.w[1]) return { err: 'input', ch: ch };
    var body = estimateBody(i), g = ch.garment, tops = ch.family === 'tops', m = ch.measures || {};
    var n = ch.sizes.length, pi = PREF[i.fit];
    var S = { i: i, ch: ch, body: body, g: g, tops: tops, n: n, pi: pi, dims: [], derived: isArr(ch.derived) ? ch.derived : [] };
    function ok(a) { return a && a.length === n; }
    if (tops) {
      var G = full(m.chest), e = easeFor(ch, g, 'chest');
      if (!ok(G) || !e) return { err: 'no-chart-data', ch: ch };
      var taper = constants.TAPER[g] || 0;
      var rc = body.chest + e[pi], rw = body.waist + e[pi] - taper;
      var waistRules = rw > rc;
      S.dims.push({ key: 'chest', G: G, R: waistRules ? rw : rc, sigma: waistRules ? constants.SIGMA.waist : constants.SIGMA.chest,
        body: body.chest, trueEase: e[1], limit: waistRules ? 'waist' : 'chest', need: Math.max(body.chest, body.waist - taper) });
    } else {
      var Gw = full(m.waist), Gh = full(m.hip);
      var elastic = ch.stretch === 'elastic-waist';
      if (!ok(Gw)) return { err: 'no-chart-data', ch: ch };
      if (elastic) {
        var eh = easeFor(ch, g, 'hip') || easeFor(ch, 'pleated', 'hip');
        if (ok(Gh)) S.dims.push({ key: 'hip', G: Gh, R: body.hip + eh[pi], sigma: constants.SIGMA.hip, body: body.hip, trueEase: eh[1], limit: 'hip', need: body.hip });
        var Gmax = full(m.waist_max);
        if (!ok(Gmax)) { Gmax = Gw.map(function (x) { return x * constants.GRADE.waistMax; }); S.derivedWaistMax = true; }
        S.waistFloor = Gmax;
        // A stretched waist is graded, not measured, unless the chart says otherwise (page charts carry no provenance).
        S.waistFloorDerived = S.derivedWaistMax || !isArr(ch.derived) || !ch.derived.length || S.derived.indexOf('waist_max') >= 0;
        if (!ok(Gh)) {
          // No hip: the stretched waist range decides alone.
          S.dims.push({ key: 'waist', G: Gw.map(function (x, k) { return (x + Gmax[k]) / 2; }), R: body.waist, sigma: constants.SIGMA.waist,
            body: body.waist, trueEase: 0, limit: 'waist', need: body.waist });
          S.noHip = true;
        }
      } else {
        var ew = easeFor(ch, g, 'waist') || easeFor(ch, 'chino', 'waist');
        var eh2 = easeFor(ch, g, 'hip') || easeFor(ch, 'chino', 'hip');
        S.dims.push({ key: 'waist', G: Gw, R: body.waist + ew[pi], sigma: constants.SIGMA.waist, body: body.waist, trueEase: ew[1], limit: 'waist', need: body.waist });
        if (ok(Gh)) S.dims.push({ key: 'hip', G: Gh, R: body.hip + eh2[pi], sigma: constants.SIGMA.hip, body: body.hip, trueEase: eh2[1], limit: 'hip', need: body.hip });
        else S.noHip = true;
        S.waistFloor = Gw;
      }
    }
    return S;
  }

  function measuresFor(S, k) {
    var ch = S.ch, m = ch.measures || {}, body = S.body, out = {};
    if (S.tops) {
      var G = full(m.chest), e = easeFor(ch, S.g, 'chest');
      var mc = G[k] - (body.chest + e[1]);
      out.chest = { delta: r1(mc), room: r1(G[k] - body.chest), word: word(mc) };
      var L = flatArr(m.length), share = constants.LENGTH_SHARE[S.g];
      if (L && L.length === S.n && share) {
        var dl = L[k] - share * S.i.h;
        out.length = { delta: r1(dl), word: lenWord(dl) };
      }
    } else {
      var Gw = full(m.waist), Gh = full(m.hip);
      if (ch.stretch === 'elastic-waist') {
        var top = S.waistFloor[k], low = Gw[k];
        var dw = body.waist > top ? top - body.waist : (body.waist < low ? low - body.waist : 0);
        out.waist = { delta: r1(dw), room: r1(top - body.waist), word: dw < 0 ? 'snug' : (dw > constants.WORD.roomy ? 'roomy' : 'true') };
        if (Gh) {
          var eh = easeFor(ch, S.g, 'hip') || easeFor(ch, 'pleated', 'hip'), mh = Gh[k] - (body.hip + eh[1]);
          out.hip = { delta: r1(mh), room: r1(Gh[k] - body.hip), word: word(mh) };
        }
      } else {
        var ew = easeFor(ch, S.g, 'waist') || easeFor(ch, 'chino', 'waist');
        var mw = Gw[k] - (body.waist + ew[1]);
        out.waist = { delta: r1(mw), room: r1(Gw[k] - body.waist), word: word(mw) };
        if (Gh) {
          var eh2 = easeFor(ch, S.g, 'hip') || easeFor(ch, 'chino', 'hip');
          var mh2 = Gh[k] - (body.hip + eh2[1]);
          out.hip = { delta: r1(mh2), room: r1(Gh[k] - body.hip), word: word(mh2) };
        }
      }
      var I = flatArr(m.inseam);
      if (I && I.length === S.n) {
        var di = I[k] - body.inseam;
        out.inseam = { delta: r1(di), word: lenWord(di) };
      }
    }
    return out;
  }

  function fail(reason, ch) {
    return { ok: false, reason: reason, family: ch ? ch.family : null, body: null, sizes: ch ? ch.sizes.slice() : [],
      best: null, size: null, garment: {}, target: {}, p: {}, between: null, pick: null, confidence: 'low', limitedBy: null, measures: {}, lengthNote: null, derivedUsed: [] };
  }

  function recommend(chart, input) {
    var S = setup(chart, input);
    if (S.err) return fail(S.err, S.ch);
    var n = S.n, sizes = S.ch.sizes, derivedUsed = [];
    // Hard floor: a size smaller than the body is never offered.
    var allowed = [], k;
    for (k = 0; k < n; k++) {
      var okk = true;
      if (S.tops) okk = S.dims[0].G[k] >= S.dims[0].need;
      else okk = S.waistFloor[k] >= S.body.waist;
      allowed.push(okk);
    }
    var anyAllowed = allowed.indexOf(true) >= 0;
    if (!anyAllowed) {
      var r0 = fail('above-range', S.ch);
      r0.body = S.body; r0.nearest = sizes[n - 1];
      r0.limitedBy = S.tops ? S.dims[0].limit : 'waist';
      for (k = 0; k < n; k++) r0.measures[sizes[k]] = measuresFor(S, k);
      return r0;
    }
    // Per measure probabilities, masked by the floor.
    var picks = [], vecs = [], floorMoved = false;
    for (var d = 0; d < S.dims.length; d++) {
      var dim = S.dims[d], raw = probs(dim.G, dim.R, dim.sigma), p = [], tot = 0;
      var rawBest = argmax(raw);
      for (k = 0; k < n; k++) { p.push(allowed[k] ? raw[k] : 0); tot += p[k]; }
      for (k = 0; k < n; k++) p[k] = tot > 0 ? p[k] / tot : 0;
      var b = argmax(p);
      if (b !== rawBest) floorMoved = true;
      picks.push(b); vecs.push(p);
    }
    var best = -1, which = 0;
    if (!vecs.length) {
      // Elastic waist without hip or waist data should not reach here; guard anyway.
      return fail('no-chart-data', S.ch);
    }
    for (d = 0; d < picks.length; d++) {
      if (picks[d] > best) { best = picks[d]; which = d; }
      else if (picks[d] === best && vecs[d][best] < vecs[which][best]) which = d;
    }
    var P = vecs[which];
    // The measure that decided the size (tops: always chest; its requirement may come from the waist, see waistRules).
    var limitedBy = S.dims[which].key;
    if (!S.tops && floorMoved && S.ch.stretch === 'elastic-waist' && S.waistFloorDerived) derivedUsed.push('waist_max');
    if (!S.tops && floorMoved) limitedBy = 'waist';
    // Between two sizes.
    var runner = -1;
    for (k = 0; k < n; k++) if (k !== best && (runner < 0 || P[k] > P[runner])) runner = k;
    var between = null, pick = { closer: sizes[best], 'true': sizes[best], relaxed: sizes[best] };
    if (runner >= 0 && P[runner] >= constants.BETWEEN) {
      var a = Math.min(best, runner), bb = Math.max(best, runner);
      between = { a: sizes[a], b: sizes[bb] };
      var ma = measuresFor(S, a), tight = false;
      for (d = 0; d < S.dims.length; d++) {
        var mk = S.dims[d].key === 'chest' ? 'chest' : S.dims[d].key;
        if (ma[mk] && ma[mk].word === 'snug') tight = true;
      }
      pick = { closer: sizes[a], 'true': tight ? sizes[bb] : sizes[a], relaxed: sizes[bb] };
    }
    var pb = P[best];
    var conf = pb >= constants.CONF.high ? 'high' : (pb >= constants.CONF.medium ? 'medium' : 'low');
    if (S.noHip) conf = down(conf);
    // A derived measure that changed the answer: one step down, never more.
    if (derivedUsed.length) conf = down(conf);
    var pm = {}, meas = {};
    for (k = 0; k < n; k++) { pm[sizes[k]] = r3(P[k]); meas[sizes[k]] = measuresFor(S, k); }
    var bm = meas[sizes[best]], ln = null;
    var lm = S.tops ? bm.length : bm.inseam;
    if (lm && lm.word !== 'true') ln = lm.word;
    // Garment circumferences per size (full cm) and the required circumference per measure for the chosen fit.
    var garment = {}, target = {}, cm = S.ch.measures || {}, circ = ['chest', 'waist', 'waist_max', 'hip', 'thigh'];
    for (k = 0; k < circ.length; k++) { var fa = full(cm[circ[k]]); if (fa && fa.length === n) garment[circ[k]] = fa; }
    for (d = 0; d < S.dims.length; d++) target[S.dims[d].key] = r1(S.dims[d].R);
    if (!S.tops && S.ch.stretch === 'elastic-waist' && !target.waist) target.waist = r1(S.body.waist);
    return {
      ok: true, reason: null, size: pick[S.i.fit === 'snug' ? 'closer' : (S.i.fit === 'loose' ? 'relaxed' : 'true')], family: S.ch.family, garmentType: S.g, garment: garment, target: target,
      waistRules: S.tops ? S.dims[0].limit === 'waist' : false, body: S.body, sizes: sizes.slice(), best: sizes[best], p: pm,
      between: between, pick: pick, confidence: conf, limitedBy: limitedBy, measures: meas, lengthNote: ln, derivedUsed: derivedUsed
    };
  }

  function fitOf(chart, input, size) {
    var S = setup(chart, input);
    if (S.err) return null;
    var k = S.ch.sizes.indexOf(String(size));
    if (k < 0) return null;
    var rec = recommend(chart, input);
    return { size: S.ch.sizes[k], p: rec.p[S.ch.sizes[k]] || 0, measures: measuresFor(S, k), best: rec.best };
  }

  window.SvntyFit = { estimateBody: estimateBody, recommend: recommend, fitOf: fitOf, toV2: toV2, constants: constants };
})();
